import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { ArrowBack } from '@mui/icons-material';
import { useMutation, useQuery } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';
import { Alert, Button, Snackbar } from '@mui/material';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import {
  OptionForm,
  OptionFormValues,
  InventoryOption,
  mapOptionToFormValues,
  mapFormValuesToInput,
  GET_INVENTORY_OPTIONS,
  GET_INVENTORY_OPTION_BY_ID,
  CREATE_INVENTORY_OPTION,
  UPDATE_INVENTORY_OPTION,
} from 'modules/inventoryOptions';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

export const OptionDetailPage = () => {
  const history = useHistory();
  const { optionId } = useParams<{ optionId?: string }>();
  const account = useSelector<any, any>(state => state.account);

  const isNew = !optionId || optionId === 'new';

  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'success' });

  const { data, loading } = useQuery<{ inventoryOptionById: InventoryOption | null }>(GET_INVENTORY_OPTION_BY_ID, {
    variables: { input: { inventoryOptionId: optionId } },
    skip: isNew,
    fetchPolicy: 'network-only',
  });

  const [createInventoryOption, { loading: creating }] = useMutation(CREATE_INVENTORY_OPTION, {
    refetchQueries: [GET_INVENTORY_OPTIONS],
  });
  const [updateInventoryOption, { loading: updating }] = useMutation(UPDATE_INVENTORY_OPTION, {
    refetchQueries: [GET_INVENTORY_OPTIONS],
  });

  const goToOptions = () => history.push('/inventory?tab=options');

  const handleSubmit = async (values: OptionFormValues) => {
    const input = mapFormValuesToInput(values);

    try {
      if (isNew) {
        await createInventoryOption({ variables: { input } });
        setSnackbar({ open: true, message: 'Option created successfully', severity: 'success' });
        goToOptions();
      } else {
        await updateInventoryOption({ variables: { input: { ...input, inventoryOptionId: optionId } } });
        setSnackbar({ open: true, message: 'Option updated successfully', severity: 'success' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.message ?? 'Failed to save option', severity: 'error' });
    }
  };

  const option = data?.inventoryOptionById ?? null;
  const title = isNew ? 'New Option' : 'Edit Option';

  return (
    <PageContainer title={title} loading={!isNew && loading && !option}>
      <CardContainer
        title={title}
        overTitleActionBtn={
          <Button startIcon={<ArrowBack />} onClick={goToOptions}>
            Back to Options
          </Button>
        }
      >
        <OptionForm
          key={`${option?.inventoryOptionId ?? 'new'}-${option?.modifiedDateTime ?? ''}`}
          accountId={account.id}
          submitting={creating || updating}
          submitLabel={isNew ? 'Create Option' : 'Save Changes'}
          defaultValues={mapOptionToFormValues(option)}
          onCancel={goToOptions}
          onSubmit={handleSubmit}
        />
      </CardContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};
